// Raw data export for the dashboard Account page.
//
// Produces a single .xlsx workbook with two tabs:
//   - "Raw Stamps": one row per stamp event, sorted by time (latest first).
//   - "Customer Details": email, registration date, email consent, number
//      of stamps, number of redeems.
//
// Reads the same `businesses/<id>` RTDB snapshot the Insights page uses and
// reuses the pure aggregation helpers in businessMetrics.js so the figures
// stay consistent across the app. Marketing consent is the one thing that
// snapshot cannot supply - it lives under customers/ - so it is read per
// customer on top. The column has to be there: a business mailing from this
// sheet must be able to see who opted out, as it already can from the iOS
// CSV export.

import * as XLSX from "xlsx";
import {
  getBusinessSnapshot,
  getMarketingConsentForCustomers,
} from "./firebase-business.js";
import { recentScans, customerSummaryRows } from "./businessMetrics.js";
import { slugifyForFilename, buildCsvTimestamp } from "./csvExport.js";

// Stamp timestamps are normalised to Unix ms by businessMetrics. Render in
// the viewer's local time; blank for anything non-numeric.
const formatStampTime = (ms) => {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleString();
};

// `registeredOn` arrives as a string of the raw RTDB value, which is either
// an ISO date string or Unix seconds (iOS legacy dual format). Render as a
// local date; fall back to the raw value if we can't parse it.
const formatRegisteredDate = (value) => {
  if (value == null || value === "") return "";
  const str = String(value);
  const asNum = Number(str);
  if (Number.isFinite(asNum) && asNum > 0) {
    return new Date(asNum * 1000).toLocaleDateString();
  }
  const ms = Date.parse(str);
  if (Number.isFinite(ms)) return new Date(ms).toLocaleDateString();
  return str;
};

// How a stored consent flag reads in the sheet. `undefined` covers customers
// enrolled before the flag existed as well as a failed read, and is kept
// distinct from "No" so a business never mistakes "we don't know" for a
// recorded opt-out, or the reverse.
export const formatEmailConsent = (consent) => {
  if (consent === true) return "Yes";
  if (consent === false) return "No";
  return "Not recorded";
};

export const CUSTOMER_DETAILS_HEADERS = [
  "Email",
  "Registration Date",
  "Email Consent",
  "Number of Stamps",
  "Number of Redeems",
];

const lookupConsent = (consentByEmail, email) => {
  if (consentByEmail instanceof Map) return consentByEmail.get(email);
  if (consentByEmail && typeof consentByEmail === "object") {
    return consentByEmail[email];
  }
  return undefined;
};

// Pure so the sheet contents can be checked without XLSX touching the
// filesystem. `summaryRows` is customerSummaryRows() output; `consentByEmail`
// is keyed by the same dotted email (a Map or plain object).
export const buildCustomerDetailsRows = (summaryRows, consentByEmail) => [
  CUSTOMER_DETAILS_HEADERS,
  ...summaryRows.map((row) => [
    row.email,
    formatRegisteredDate(row.registeredOn),
    formatEmailConsent(lookupConsent(consentByEmail, row.email)),
    row.stamps,
    row.redemptions,
  ]),
];

export const buildStampRows = (stamps) => [
  ["Time", "Customer Email", "Activity"],
  ...recentScans(stamps).map((row) => [
    formatStampTime(row.timestamp),
    row.email,
    row.activity || "",
  ]),
];

/**
 * Build and download an .xlsx export of a business's raw data.
 * @param {string} businessId - RTDB business id to export.
 * @param {string} [displayName] - Human name used for the filename slug.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const exportRawData = async (businessId, displayName) => {
  if (!businessId) {
    return { success: false, message: "No business selected." };
  }

  let snapshot;
  try {
    snapshot = await getBusinessSnapshot(businessId);
  } catch (e) {
    return {
      success: false,
      message: "We couldn't load your data. Please try again in a moment.",
    };
  }

  if (snapshot == null) {
    return { success: false, message: "There's no data to export yet." };
  }

  const { stamps, customers, redeems } = snapshot;

  // Tab 1: raw stamps, latest first. recentScans returns rows sorted desc.
  const stampRows = buildStampRows(stamps);

  // Tab 2: customer details. The emptiness check comes before the consent
  // reads so an empty business does not pay for a round trip it cannot use.
  const summaryRows = customerSummaryRows(customers, stamps, redeems);
  if (stampRows.length === 1 && summaryRows.length === 0) {
    return { success: false, message: "There's no data to export yet." };
  }

  // The helper already shields each read; this guards the batch as a whole so
  // the workbook still downloads (with "Not recorded") if it ever threw.
  const consentByEmail = await getMarketingConsentForCustomers(
    businessId,
    summaryRows.map((row) => row.email)
  ).catch(() => new Map());
  const customerRows = buildCustomerDetailsRows(summaryRows, consentByEmail);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.aoa_to_sheet(stampRows),
    "Raw Stamps"
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.aoa_to_sheet(customerRows),
    "Customer Details"
  );

  const slug = slugifyForFilename(displayName || businessId || "business");
  XLSX.writeFile(workbook, `${slug}-raw-data-${buildCsvTimestamp()}.xlsx`);

  return {
    success: true,
    message: `Exported ${stampRows.length - 1} stamps and ${
      customerRows.length - 1
    } customers.`,
  };
};
